import { FormControl, FormGroup, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { OverOsobuDotazDto } from '@generated/be';
import { TextValidators } from '@shared/utils';
import { OsobaValidationErrors, OsobaValidators } from '../misc/validators';

/** Pole osoby ve filtrech přehledů (majitel certifikátu, zastupovaná / zastupující osoba oprávnění). */
export type OsobaFormControls = {
  jmeno: FormControl<string | null>;
  prijmeni: FormControl<string | null>;
  dokladTotoznosti: FormGroup<{
    cislo: FormControl<string | null>;
    typ: FormControl<string | null>;
  }>;
  datumNarozeni: FormControl<string | null>;
  // nazev: odstraněno na základě DIAREZA-1881
  ico: FormControl<number | null>;
};

export type OsobaAifoFormControls = OsobaFormControls & { aifo: FormControl<string | null> };

export interface OsobaFormOptions {
  /** Validátory celé skupiny (např. `OsobaValidators.validatePhysicalPerson()`). */
  validators?: ValidatorFn[];
  /** Párová validace dokladu totožnosti (`OsobaValidators.validateDokladTotoznosti`), výchozí `true`. */
  validateDoklad?: boolean;
  /** Přidá pole `aifo` (filtr certifikátů). */
  aifo?: boolean;
}

/**
 * Skupina polí osoby ve filtru přehledu: jméno, příjmení, doklad totožnosti (číslo + druh), datum narození, IČO.
 * State služby přehledů čtou `form.value` – tvar, pořadí klíčů ani validace neměnit.
 */
export function osobaFormGroup(opts: OsobaFormOptions & { aifo: true }): FormGroup<OsobaAifoFormControls>;
export function osobaFormGroup(opts?: OsobaFormOptions): FormGroup<OsobaFormControls>;
export function osobaFormGroup({ validators, validateDoklad = true, aifo = false }: OsobaFormOptions = {}):
  FormGroup<OsobaFormControls> | FormGroup<OsobaAifoFormControls> {
  const controls: OsobaFormControls = {
    jmeno: new FormControl<string | null>(null),
    prijmeni: new FormControl<string | null>(null),
    dokladTotoznosti: new FormGroup(
      {
        cislo: new FormControl<string | null>(null),
        typ: new FormControl<string | null>(null),
      },
      validateDoklad ? { validators: OsobaValidators.validateDokladTotoznosti } : undefined
    ),
    datumNarozeni: new FormControl<string | null>(null),
    ico: new FormControl<number | null>(null, [Validators.max(1000000000)]),
  };
  if (aifo) {
    return new FormGroup({ ...controls, aifo: new FormControl<string | null>(null) }, validators);
  }
  return new FormGroup(controls, validators);
}

/** IČO zadávané jako text: 1–8 číslic. */
export const icoPatternValidator: ValidatorFn = Validators.pattern(/^\d{1,8}$/);

/**
 * Údaje zastupující / zastupované osoby v ověření oprávnění a jeho reklamaci (přepínač FO/PO, `isFyzickaOsoba`
 * drží obalující skupina – `zastupFormGroup()`). IČO je textové (`icoPatternValidator`); v režimu FO ho komponenta
 * formuláře vypíná.
 */
export function zastupOsobaFormGroup(): UntypedFormGroup {
  return new UntypedFormGroup(
    {
      jmeno: new FormControl<string | null>(null),
      prijmeni: new FormControl<string | null>(null),
      dokladTotoznosti: new FormGroup({
        typ: new FormControl<string | null>(null),
        cislo: new FormControl<string | null>(null),
      }),
      datumNarozeni: new FormControl<string | null>(null),
      nazev: new FormControl<string | null>(null),
      ico: new FormControl<string | null>(null, [Validators.required, icoPatternValidator]),
    },
    [OsobaValidators.validatePhysicalPerson([OsobaValidationErrors.MissingRequiredCombination])]
  );
}

/** Povinný důvod (ověření, nahlížení na osobní údaje ve filtrech); nesmí obsahovat jen mezery. */
export function duvodControl(): FormControl<string | null> {
  return new FormControl<string | null>(null, [Validators.required, TextValidators.noWhitespaces()]);
}

export type ZastupFormControls = {
  isFyzickaOsoba: FormControl<boolean | null>;
  zastupOsoba: UntypedFormGroup;
};

export type ZastupDuvodFormControls = ZastupFormControls & { duvod: FormControl<string | null> };

/**
 * Obalující skupina formuláře osoby FO/PO (`overeni-opr-osoba-fo-po-form`): `isFyzickaOsoba` (výchozí FO),
 * `zastupOsoba` a volitelně povinný `duvod`. `form.value` se posílá na BE – tvar ani pořadí klíčů neměnit.
 */
export function zastupFormGroup(opts: { duvod: true }): FormGroup<ZastupDuvodFormControls>;
export function zastupFormGroup(opts?: { duvod?: false }): FormGroup<ZastupFormControls>;
export function zastupFormGroup({ duvod = false }: { duvod?: boolean } = {}):
  FormGroup<ZastupFormControls> | FormGroup<ZastupDuvodFormControls> {
  const controls: ZastupFormControls = {
    isFyzickaOsoba: new FormControl<boolean>(true),
    zastupOsoba: zastupOsobaFormGroup(),
  };
  return duvod ? new FormGroup({ ...controls, duvod: duvodControl() }) : new FormGroup(controls);
}

export enum SubmitFormResultType {
  Osoba,
  Null,
}

/** Výsledek `submitForm()` formuláře osoby (ověření oprávnění, reklamace implicitního oprávnění). */
export interface SubmitFormResult {
  type: SubmitFormResultType;
  value?: string | OverOsobuDotazDto;
  duvod?: string | null;
}
